import {
  type FdbAttribution,
  type FdbOverrideReasonCode,
  type PrescriptionDraft,
  type StructuredSig,
  StructuredSigSchema,
} from "@emr/contracts";
import { type FormEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import type {
  PrintablePrescriptionRecord,
  UpdateWorkforceProfileRequest,
  WorkforceProfile,
} from "../api/clinicalWorkspace";
import {
  type PrintablePrescriptionDraft,
  prescriptionVerificationLabel,
  verifiedPrescriptionPdfBytes,
} from "../lib/printablePrescription";
import { FdbMedicationInput, type FdbMedicationSearch } from "./FdbPrescribing";
import { Icon } from "./Icon";

/**
 * Shared printable-prescription form pieces. The persisted right-rail panel,
 * the Horizon Rx dialog, and the AI prescription actionable card all render
 * these exact fields so there is exactly one signing form implementation.
 */

export interface PrescriptionFormValues {
  readonly medicationDisplay: string;
  readonly sig: string;
  readonly quantity: string;
  readonly quantityUnit: string;
  readonly refills: string;
  readonly indication: string;
  readonly substitutionAllowed: boolean;
  /**
   * FDB MEDID picked from MedKnowledge search. Typing in the medication field
   * clears it: a coded prescription is exactly the picked product.
   */
  readonly medicationCode?: string | undefined;
  /**
   * "ai_draft" while the medication is still what the AI drafted: FDB codes it
   * without showing the search list. Typing in the field clears it.
   */
  readonly medicationSource?: "ai_draft" | undefined;
  /** FDB attribution from the search that coded the medication (printed on the pad). */
  readonly fdbAttribution?: FdbAttribution | null | undefined;
  /** Structured dose for FDB dose-range checking; all optional, all-or-nothing. */
  readonly doseAmount?: string | undefined;
  readonly doseUnit?: string | undefined;
  readonly frequencyPerDay?: string | undefined;
  readonly durationDays?: string | undefined;
  /** Override reasons chosen for screening alerts, keyed by finding key. */
  readonly overrides?: Readonly<Record<string, FdbOverrideChoice>> | undefined;
}

export interface FdbOverrideChoice {
  readonly reasonCode: FdbOverrideReasonCode;
  readonly reasonText?: string | undefined;
}

/**
 * The structured dose from the form, or null when none was entered. A partly
 * entered dose is an error so a half-typed sig never silently skips checking.
 */
export function structuredSigFromFormValues(
  values: PrescriptionFormValues,
):
  | { readonly ok: true; readonly sig: StructuredSig | null }
  | { readonly ok: false; readonly message: string } {
  const amount = (values.doseAmount ?? "").trim();
  const frequency = (values.frequencyPerDay ?? "").trim();
  const duration = (values.durationDays ?? "").trim();
  if (amount === "" && frequency === "" && duration === "") return { ok: true, sig: null };
  const parsed = StructuredSigSchema.safeParse({
    doseAmount: Number(amount),
    doseUnit: (values.doseUnit ?? "mg").trim(),
    frequencyPerDay: Number(frequency),
    ...(duration === "" ? {} : { durationDays: Number(duration) }),
  });
  if (!parsed.success || amount === "" || frequency === "") {
    return {
      ok: false,
      message:
        "Complete the dose (amount, unit and times per day, with a whole number of days) or clear it.",
    };
  }
  return { ok: true, sig: parsed.data };
}

export const emptyPrescriptionFormValues: PrescriptionFormValues = {
  medicationDisplay: "",
  sig: "",
  quantity: "",
  quantityUnit: "tablets",
  refills: "0",
  indication: "",
  substitutionAllowed: true,
};

export interface PrescriptionProposalText {
  readonly title: string;
  readonly detail: string;
  readonly rationale: string;
}

const quantityUnitPattern =
  /\b\d{1,4}(?:\.\d+)?\s*(tablets?|tabs?|capsules?|caps?|puffs?|sprays?|patches?|lozenges?|sachets?|vials?|units?|doses?|ml)\b/i;

const proposalQuantityPattern = /(?:quantity|qty|dispense|supply)\s*[:#]?\s*(\d{1,4}(?:\.\d+)?)/i;

const proposalUnitDefaults: Record<string, string> = {
  tab: "tablets",
  tabs: "tablets",
  tablet: "tablets",
  cap: "capsules",
  caps: "capsules",
  capsule: "capsules",
};

/**
 * Best-effort prefill from an AI plan proposal (title/detail/rationale free
 * text). The dose stays implicit in the medication string; anything that does
 * not parse stays editable free text for the clinician.
 */
export function prescriptionFormValuesFromProposal(
  proposal: PrescriptionProposalText,
): PrescriptionFormValues {
  const text = `${proposal.title} ${proposal.detail}`;
  const unitMatch = text.match(quantityUnitPattern);
  const quantityMatch =
    text.match(proposalQuantityPattern) ??
    text.match(/#\s*(\d{1,4})\b/) ??
    unitMatch?.[0]?.match(/\d{1,4}(?:\.\d+)?/) ??
    null;
  const refillsMatch = text.match(/(\d{1,2})\s*refills?/i);
  // "for <indication>" — skipping duration phrases like "for 10 days".
  const indicationMatch =
    proposal.detail.match(/\bfor\s+(?!\d)([^.;:]{3,120})/i) ??
    proposal.rationale.match(/\bfor\s+(?!\d)([^.;:]{3,120})/i);
  const rawUnit = unitMatch?.[1]?.toLowerCase();
  return {
    medicationDisplay: proposal.title.trim(),
    medicationSource: "ai_draft",
    sig: proposal.detail.trim(),
    quantity: quantityMatch?.[1] ?? quantityMatch?.[0] ?? "",
    quantityUnit: rawUnit === undefined ? "tablets" : (proposalUnitDefaults[rawUnit] ?? rawUnit),
    refills: /no\s+refills?/i.test(text) ? "0" : (refillsMatch?.[1] ?? "0"),
    indication: (indicationMatch?.[1] ?? proposal.rationale).trim(),
    substitutionAllowed: true,
  };
}

/**
 * Exact prefill from a structured AI prescription draft: every form field maps
 * one-to-one, nothing is parsed from prose, and everything stays editable for
 * the physician to approve, change, or discard before signing.
 */
export function prescriptionFormValuesFromDraft(draft: PrescriptionDraft): PrescriptionFormValues {
  return {
    medicationDisplay: draft.medicationDisplay,
    medicationSource: "ai_draft",
    sig: draft.sig,
    quantity: String(draft.quantity),
    quantityUnit: draft.quantityUnit,
    refills: String(draft.refills),
    indication: draft.indication,
    substitutionAllowed: draft.substitutionAllowed,
  };
}

export type PrintableDraftValidation =
  | { readonly ok: true; readonly draft: PrintablePrescriptionDraft }
  | { readonly ok: false; readonly message: string };

/**
 * Client-side mirror of the strict server payload rules so a batch approval
 * fails with a readable message instead of an opaque schema error.
 */
export function printableDraftFromFormValues(
  values: PrescriptionFormValues,
): PrintableDraftValidation {
  const quantity = Number(values.quantity);
  const refills = Number(values.refills);
  if (values.medicationDisplay.trim().length < 2) {
    return { ok: false, message: "Enter the medication, strength, and form before signing." };
  }
  if (values.sig.trim().length < 2) {
    return { ok: false, message: "Enter the sig (directions) before signing." };
  }
  if (values.quantity.trim().length === 0 || !Number.isFinite(quantity) || quantity <= 0) {
    return { ok: false, message: "Enter a quantity greater than zero before signing." };
  }
  if (values.quantityUnit.trim().length === 0) {
    return { ok: false, message: "Enter a quantity unit before signing." };
  }
  if (!Number.isInteger(refills) || refills < 0 || refills > 12) {
    return { ok: false, message: "Refills must be a whole number between 0 and 12." };
  }
  if (values.indication.trim().length < 2) {
    return { ok: false, message: "Enter the indication before signing." };
  }
  const structured = structuredSigFromFormValues(values);
  if (!structured.ok) return structured;
  const overrides = Object.entries(values.overrides ?? {}).map(([findingKey, choice]) => ({
    findingKey,
    reasonCode: choice.reasonCode,
    ...(choice.reasonText?.trim() ? { reasonText: choice.reasonText.trim() } : {}),
  }));
  return {
    ok: true,
    draft: {
      medicationDisplay: values.medicationDisplay,
      sig: values.sig,
      quantity,
      quantityUnit: values.quantityUnit,
      refills,
      indication: values.indication,
      substitutionAllowed: values.substitutionAllowed,
      ...(values.medicationCode === undefined
        ? {}
        : { medicationCode: values.medicationCode, attribution: values.fdbAttribution ?? null }),
      ...(structured.sig === null ? {} : { structuredSig: structured.sig }),
      ...(overrides.length === 0 ? {} : { overrides }),
    },
  };
}

/**
 * The exact editable prescription fields (medication, sig, quantity, refills,
 * indication, substitution).
 *
 * Every control carries a stable id/name and opts out of browser autofill.
 * Free-text medical fields with no name look like address/organization inputs
 * to Chrome's autofill heuristics, and a re-run of autofill (triggered by a
 * layout change such as dragging this dialog or resizing the window) would
 * clear the clinician's typed medication before React saw an input event.
 */
export function PrescriptionFormFields({
  values,
  onChange,
  disabled = false,
  idPrefix,
  fdbSearch,
}: {
  readonly values: PrescriptionFormValues;
  readonly onChange: (values: PrescriptionFormValues) => void;
  readonly disabled?: boolean;
  /** Unique per rendered form so ids/names never collide across cards. */
  readonly idPrefix: string;
  /**
   * FDB MedKnowledge search. When present the medication field offers FDB
   * matches and the dose fields feed dose-range checking.
   */
  readonly fdbSearch?: FdbMedicationSearch | undefined;
}) {
  const set = <Key extends keyof PrescriptionFormValues>(
    key: Key,
    value: PrescriptionFormValues[Key],
  ) => onChange({ ...values, [key]: value });
  const fieldId = (field: string) => `${idPrefix}-${field}`;
  const typeMedication = (text: string) =>
    onChange({
      ...values,
      medicationDisplay: text,
      medicationSource: undefined,
      medicationCode: undefined,
      fdbAttribution: undefined,
      overrides: undefined,
    });

  return (
    <fieldset disabled={disabled}>
      <legend className="sr-only">Printable prescription details</legend>
      <label htmlFor={fieldId("medication")}>
        Medication
        {fdbSearch === undefined ? (
          <input
            required
            id={fieldId("medication")}
            name={fieldId("medication")}
            autoComplete="off"
            value={values.medicationDisplay}
            onChange={(event) => typeMedication(event.target.value)}
            placeholder="Medication, strength, and form"
          />
        ) : (
          <FdbMedicationInput
            search={fdbSearch}
            values={values}
            id={fieldId("medication")}
            name={fieldId("medication")}
            label="Medication"
            placeholder="Start typing a medication to search FDB"
            onType={typeMedication}
            onPick={(medication, attribution) =>
              onChange({
                ...values,
                // Show the actual selected product, including an automatically matched brand.
                medicationDisplay: medication.display,
                medicationCode: medication.code,
                fdbAttribution: attribution,
                overrides: undefined,
              })
            }
          />
        )}
      </label>
      <label htmlFor={fieldId("sig")}>
        Sig (directions)
        <textarea
          required
          id={fieldId("sig")}
          name={fieldId("sig")}
          autoComplete="off"
          rows={2}
          value={values.sig}
          onChange={(event) => set("sig", event.target.value)}
        />
      </label>
      {fdbSearch === undefined ? null : (
        <div className="rx-structured-dose">
          <label htmlFor={fieldId("dose-amount")}>
            Dose
            <input
              id={fieldId("dose-amount")}
              name={fieldId("dose-amount")}
              autoComplete="off"
              type="number"
              min="0"
              step="any"
              value={values.doseAmount ?? ""}
              onChange={(event) => set("doseAmount", event.target.value)}
            />
          </label>
          <label htmlFor={fieldId("dose-unit")}>
            Unit
            <select
              id={fieldId("dose-unit")}
              name={fieldId("dose-unit")}
              value={values.doseUnit ?? "mg"}
              onChange={(event) => set("doseUnit", event.target.value)}
            >
              <option value="mg">mg</option>
              <option value="g">g</option>
              <option value="mcg">mcg</option>
            </select>
          </label>
          <label htmlFor={fieldId("frequency")}>
            Times per day
            <input
              id={fieldId("frequency")}
              name={fieldId("frequency")}
              autoComplete="off"
              type="number"
              min="0"
              step="any"
              value={values.frequencyPerDay ?? ""}
              onChange={(event) => set("frequencyPerDay", event.target.value)}
            />
          </label>
          <label htmlFor={fieldId("duration")}>
            Days
            <input
              id={fieldId("duration")}
              name={fieldId("duration")}
              autoComplete="off"
              type="number"
              min="1"
              step="1"
              value={values.durationDays ?? ""}
              onChange={(event) => set("durationDays", event.target.value)}
            />
          </label>
        </div>
      )}
      <label htmlFor={fieldId("quantity")}>
        Quantity
        <input
          required
          id={fieldId("quantity")}
          name={fieldId("quantity")}
          autoComplete="off"
          type="number"
          min="1"
          step="any"
          value={values.quantity}
          onChange={(event) => set("quantity", event.target.value)}
        />
      </label>
      <label htmlFor={fieldId("quantity-unit")}>
        Quantity unit
        <input
          required
          id={fieldId("quantity-unit")}
          name={fieldId("quantity-unit")}
          autoComplete="off"
          value={values.quantityUnit}
          onChange={(event) => set("quantityUnit", event.target.value)}
        />
      </label>
      <label htmlFor={fieldId("refills")}>
        Refills
        <input
          required
          id={fieldId("refills")}
          name={fieldId("refills")}
          autoComplete="off"
          type="number"
          min="0"
          max="12"
          step="1"
          value={values.refills}
          onChange={(event) => set("refills", event.target.value)}
        />
      </label>
      <label htmlFor={fieldId("indication")}>
        Indication
        <input
          required
          id={fieldId("indication")}
          name={fieldId("indication")}
          autoComplete="off"
          value={values.indication}
          onChange={(event) => set("indication", event.target.value)}
        />
      </label>
      <label className="checkbox-label" htmlFor={fieldId("substitution")}>
        <input
          type="checkbox"
          id={fieldId("substitution")}
          name={fieldId("substitution")}
          checked={values.substitutionAllowed}
          onChange={(event) => set("substitutionAllowed", event.target.checked)}
        />
        Substitution permitted
      </label>
    </fieldset>
  );
}

/**
 * Inline workforce-profile mini-form shown when the licence fields required
 * for signing are missing (the RPC 422s without them).
 */
export function PrescriberCredentialForm({
  profile,
  save,
}: {
  readonly profile: WorkforceProfile;
  /**
   * usePrintablePrescriptionSigning's saveProfile: persists the credentials
   * and syncs the shared profile cache so sign() can wait on the save.
   */
  readonly save: (update: UpdateWorkforceProfileRequest) => Promise<WorkforceProfile>;
}) {
  const [licenseNumber, setLicenseNumber] = useState(profile.licenseNumber ?? "");
  const [licenseJurisdiction, setLicenseJurisdiction] = useState(profile.licenseJurisdiction ?? "");
  const [signatureName, setSignatureName] = useState(profile.signatureName ?? profile.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await save({
        expectedVersion: profile.profileVersion,
        displayName: profile.displayName,
        licenseNumber: licenseNumber.trim(),
        licenseJurisdiction: licenseJurisdiction.trim(),
        signatureName: signatureName.trim(),
      });
    } catch (updateError) {
      setError(
        updateError instanceof Error
          ? updateError.message
          : "The prescriber profile could not be updated",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="prescriber-credential-form" onSubmit={(event) => void submit(event)}>
      <p>
        <Icon name="shield" />
        Signing requires your licence and signature name on the workforce profile.
      </p>
      <label htmlFor="prescriber-licence-number">
        Licence number
        <input
          required
          id="prescriber-licence-number"
          name="prescriber-licence-number"
          autoComplete="off"
          value={licenseNumber}
          onChange={(event) => setLicenseNumber(event.target.value)}
        />
      </label>
      <label htmlFor="prescriber-licence-jurisdiction">
        Licence jurisdiction
        <input
          required
          id="prescriber-licence-jurisdiction"
          name="prescriber-licence-jurisdiction"
          autoComplete="off"
          value={licenseJurisdiction}
          onChange={(event) => setLicenseJurisdiction(event.target.value)}
        />
      </label>
      <label htmlFor="prescriber-signature-name">
        Signature name
        <input
          required
          id="prescriber-signature-name"
          name="prescriber-signature-name"
          autoComplete="off"
          value={signatureName}
          onChange={(event) => setSignatureName(event.target.value)}
        />
      </label>
      {error ? (
        <p className="workflow-blocker" role="alert">
          <Icon name="alert" /> {error}
        </p>
      ) : null}
      <button type="submit" className="secondary-button" disabled={saving}>
        {saving ? "Saving prescriber profile…" : "Save prescriber profile"}
      </button>
    </form>
  );
}

/** Signed receipt summary without technical hash or certificate details. */
export function SignedPrescriptionSummary({
  record,
  actions,
}: {
  readonly record: PrintablePrescriptionRecord;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="signed-prescription-confirmation" role="status">
      <p>
        <Icon name="check" />
        <strong>Signed printable prescription</strong>
      </p>
      <dl>
        <div>
          <dt>Status</dt>
          <dd>
            {record.status} · {record.deliveryMethod}
          </dd>
        </div>
        <div>
          <dt>Signed at</dt>
          <dd>
            <time dateTime={record.signedAt}>{record.signedAt}</time>
          </dd>
        </div>
        <div>
          <dt>Prescriber</dt>
          <dd>
            {record.prescriber?.signatureName ?? "Signed clinician"}
            {record.prescriber?.licenseNumber
              ? ` · Licence ${record.prescriber.licenseNumber}`
              : ""}
          </dd>
        </div>
        <div>
          <dt>Document</dt>
          <dd>{record.fileName}</dd>
        </div>
        <div>
          <dt>Verification</dt>
          <dd>{prescriptionVerificationLabel(record)}</dd>
        </div>
      </dl>
      {actions}
    </div>
  );
}

/** A printable document open in the in-app viewer: raw bytes, not a blob URL. */
export interface PrintablePdfPreviewState {
  readonly bytes: Uint8Array;
  readonly fileName: string;
  /** Accessible name of the viewer dialog. */
  readonly label: string;
  /** Small header line rendered above the file name. */
  readonly eyebrow: string;
}

export interface PrintablePdfActions {
  /** Opens the signed PDF in the in-app viewer (never a popup window). */
  readonly viewPdf: (record: PrintablePrescriptionRecord) => void;
  /** Opens any PDF (e.g. the combined package) in the same viewer. */
  readonly viewBytes: (
    bytes: Uint8Array,
    document: { readonly fileName: string; readonly label: string; readonly eyebrow: string },
  ) => void;
  /** The open document, or null. Render it with the printable document viewer. */
  readonly preview: PrintablePdfPreviewState | null;
  /** Closes the viewer. */
  readonly closePreview: () => void;
  /** Prints the signed PDF through the caller-rendered hidden iframe. */
  readonly printPdf: (record: PrintablePrescriptionRecord) => void;
  /** Prints an arbitrary PDF blob (e.g. a combined document) through the same iframe. */
  readonly printBlob: (blob: Blob) => void;
  readonly printFrameRef: RefObject<HTMLIFrameElement | null>;
  readonly handlePrintFrameLoad: () => void;
}

/**
 * View/Print actions for printable documents. The caller renders
 * `<iframe ref={printFrameRef} className="demo-pdf-print-frame" … onLoad={handlePrintFrameLoad} />`
 * for printing and the printable document viewer for `preview`.
 *
 * Viewing hands raw bytes to a pdf.js viewer rather than pointing an iframe at
 * a blob URL: a browser without the built-in PDF plugin renders such an iframe
 * blank, so the document silently never appeared. Object URLs are created only
 * for printing, and every one is revoked when the owning component unmounts.
 */
export function usePrintablePdfActions(): PrintablePdfActions {
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const printPendingRef = useRef(false);
  const objectUrlsRef = useRef<string[]>([]);
  const [preview, setPreview] = useState<PrintablePdfPreviewState | null>(null);

  useEffect(
    () => () => {
      for (const url of objectUrlsRef.current) window.URL.revokeObjectURL(url);
      objectUrlsRef.current = [];
    },
    [],
  );

  const printBlob = (blob: Blob) => {
    const frame = printFrameRef.current;
    if (!frame) return;
    const url = window.URL.createObjectURL(blob);
    objectUrlsRef.current.push(url);
    printPendingRef.current = true;
    frame.src = url;
  };

  return {
    printFrameRef,
    preview,
    viewPdf: (record) => {
      if (record.verification !== "verified") {
        setPreview(null);
        return;
      }
      setPreview({
        bytes: verifiedPrescriptionPdfBytes(record),
        fileName: record.fileName,
        label: "Signed prescription PDF",
        eyebrow: "Signed document · Verified · print only",
      });
    },
    viewBytes: (bytes, document) => setPreview({ bytes, ...document }),
    closePreview: () => setPreview(null),
    printPdf: (record) => {
      if (record.verification !== "verified") return;
      printBlob(
        new Blob([verifiedPrescriptionPdfBytes(record).buffer as ArrayBuffer], {
          type: "application/pdf",
        }),
      );
    },
    printBlob,
    handlePrintFrameLoad: () => {
      if (!printPendingRef.current) return;
      printPendingRef.current = false;
      const frameWindow = printFrameRef.current?.contentWindow;
      frameWindow?.focus();
      frameWindow?.print();
    },
  };
}
